import {
  type DoNotDisturb,
  type EventDeliveryView,
  type EventTypeView,
  UNCALLABLE_EVENT_TYPES,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { useEffect, useState } from 'react';
import {
  SettingRow,
  SettingSelect,
  SettingsGroup,
  SettingsNote,
  SettingToggle,
  SettingValue,
} from '@/components/settings-page/setting-controls';
import { electronInvoke, isElectronDesktop } from '@/lib/electron-bridge';
import {
  pushPermission,
  pushSubscribed,
  pushSupport,
  subscribeToPush,
  unsubscribeFromPush,
} from '@/lib/push-notifications';
import {
  DO_NOT_DISTURB_CHOICES,
  type DoNotDisturbChoice,
  doNotDisturbSummary,
  loadDoNotDisturb,
  saveDoNotDisturb,
} from '@/lib/settings-page/do-not-disturb';

/** The event "Send a test notification" publishes. */
const TEST_EVENT_TYPE = 'crowdex/notification.test';

/** The levels of D17, from nowhere to the assistant ringing. */
const DELIVERY_LEVELS: readonly {
  value: EventDeliveryView;
  label: string;
  meaning: string;
}[] = [
  { value: 'off', label: 'Off', meaning: 'nowhere' },
  { value: 'list', label: 'List', meaning: 'only in the bell' },
  { value: 'toast', label: 'Toast', meaning: 'in the app' },
  { value: 'native', label: 'System', meaning: 'also when Crowdex is closed' },
  { value: 'call', label: 'Call', meaning: 'the assistant rings' },
];

/** The levels a type offers: some cannot ring. */
export function levelsFor(type: string) {
  return UNCALLABLE_EVENT_TYPES.includes(type)
    ? DELIVERY_LEVELS.filter((level) => level.value !== 'call')
    : DELIVERY_LEVELS;
}

/** Groups the types by who publishes them, Crowdex first. */
export function groupBySource(types: readonly EventTypeView[]) {
  const groups = new Map<string, { title: string; types: EventTypeView[] }>();
  for (const type of types) {
    const key = type.source.plugin ?? '';
    const group = groups.get(key) ?? { title: type.source.title, types: [] };
    group.types.push(type);
    groups.set(key, group);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => (left === '' ? -1 : right === '' ? 1 : left.localeCompare(right)))
    .map(([, group]) => group);
}

export type EventTone = 'info' | 'success' | 'warning' | 'error';

/** What kind of news a type is, from its name (`…failed` is an error, `…waiting` waits on you). */
export function eventTone(type: string): EventTone {
  const name = type.split('/').at(-1) ?? type;
  if (/fail|error|expired|denied/.test(name)) return 'error';
  if (/wait|ring|approv|question|needs/.test(name)) return 'warning';
  if (/succe|finish|done|replied|merged|complete|export/.test(name)) return 'success';
  return 'info';
}

/** A type's dot: amber only for what waits on the user (`waits`), its name's news otherwise. */
export function typeTone(type: Pick<EventTypeView, 'type' | 'waits'>): EventTone {
  if (type.waits) return 'warning';
  const tone = eventTone(type.type);
  return tone === 'warning' ? 'info' : tone;
}

const TONE_CLASS: Record<EventTone, string> = {
  info: 'bg-running',
  success: 'bg-success',
  warning: 'bg-attention',
  error: 'bg-destructive',
};

function ToneDot({ tone }: { tone: EventTone }) {
  return <span aria-hidden className={cn('size-1.5 shrink-0 rounded-full', TONE_CLASS[tone])} />;
}

/** System notifications on this device: pushed by the server to a browser, shown by the Desktop. */
function SystemNotificationsRow({ phone }: { phone: boolean }) {
  const support = pushSupport();
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (support !== 'supported') return;
    let cancelled = false;
    void pushSubscribed().then(
      (current) => !cancelled && setSubscribed(current),
      () => !cancelled && setSubscribed(false),
    );
    return () => {
      cancelled = true;
    };
  }, [support]);
  const [desktopSupported, setDesktopSupported] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isElectronDesktop()) return;
    let live = true;
    electronInvoke<boolean>('notifications_supported').then(
      (supported) => live && setDesktopSupported(supported === true),
      () => live && setDesktopSupported(false),
    );
    return () => {
      live = false;
    };
  }, []);
  const title = phone ? 'Push notifications' : 'System notifications';
  if (isElectronDesktop())
    return (
      <SettingRow
        description={
          desktopSupported === false
            ? 'This system shows no notifications for Crowdex'
            : 'Allowed for Crowdex in the system’s notification settings'
        }
        title={title}
      >
        {desktopSupported !== null && (
          <SettingValue tone={desktopSupported ? 'success' : 'muted'}>
            {desktopSupported ? 'On' : 'Unavailable'}
          </SettingValue>
        )}
      </SettingRow>
    );
  if (support === 'unsupported')
    return (
      <SettingRow description="This browser cannot show them" title={title}>
        <SettingValue>Unavailable</SettingValue>
      </SettingRow>
    );
  if (support === 'install-first')
    return (
      <SettingRow
        description="Add Crowdex to your Home Screen (Share, then Add to Home Screen) and open it from there"
        title={title}
      >
        <SettingValue tone="attention">Install first</SettingValue>
      </SettingRow>
    );
  const change = (next: boolean) => {
    setError(null);
    setSubscribed(next);
    void (next ? subscribeToPush() : unsubscribeFromPush().then(() => false)).then(
      (now) => {
        setSubscribed(now);
        if (next && !now)
          setError(
            pushPermission() === 'denied'
              ? 'Blocked in this browser’s settings'
              : 'Notifications were not allowed',
          );
      },
      (reason: unknown) => {
        setSubscribed(!next);
        setError(reason instanceof Error ? reason.message : String(reason));
      },
    );
  };
  const installed = window.matchMedia?.('(display-mode: standalone)').matches === true;
  return (
    <SettingRow
      description={
        error !== null ? (
          <span className="text-destructive">{error}</span>
        ) : subscribed ? (
          <span className="text-success">
            Allowed{installed ? ' · installed app' : ' in this browser'}
          </span>
        ) : (
          'Even when Crowdex is closed'
        )
      }
      title={title}
    >
      <SettingToggle
        checked={subscribed === true}
        label={`${title} on this device`}
        onChange={change}
      />
    </SettingRow>
  );
}

/** What the test notification became, as the server told it. */
const TEST_RESULT: Record<EventDeliveryView, string> = {
  off: 'Sent: its type is off, so it was not shown.',
  list: 'Sent: it is in the list (Do not disturb holds the rest).',
  toast: 'Sent: shown in the app.',
  native: 'Sent: shown as a system notification.',
  call: 'Sent: the assistant rings.',
};

/** A real event, told the way every other one is: system notification, push, toast. */
function TestNotificationRow() {
  const [state, setState] = useState<{ sending: boolean; result: string | null }>({
    sending: false,
    result: null,
  });
  const send = () => {
    setState({ sending: true, result: null });
    apiFetch<{ delivery: EventDeliveryView }>('/api/notifications/test', { method: 'POST' }).then(
      ({ delivery }) => setState({ sending: false, result: TEST_RESULT[delivery] }),
      (reason: unknown) =>
        setState({
          sending: false,
          result: `Could not send it: ${reason instanceof Error ? reason.message : String(reason)}`,
        }),
    );
  };
  return (
    <SettingRow description={state.result ?? undefined} title="Send a test notification">
      <Button disabled={state.sending} onClick={send} size="sm" variant="outline">
        {state.sending ? 'Sending…' : 'Send'}
      </Button>
    </SettingRow>
  );
}

function DoNotDisturbRow() {
  const [state, setState] = useState<DoNotDisturb | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadDoNotDisturb().then(
      (loaded) => live && setState(loaded),
      () => live && setState({ choice: 'off', until: null }),
    );
    return () => {
      live = false;
    };
  }, []);
  return (
    <SettingRow
      description={
        error !== null ? (
          <span className="text-destructive">{error}</span>
        ) : (
          doNotDisturbSummary(state ?? { choice: 'off', until: null })
        )
      }
      title="Do not disturb"
    >
      <SettingSelect
        disabled={state === null}
        label="Do not disturb"
        onValueChange={(value) => {
          const previous = state;
          setError(null);
          setState({ choice: value as DoNotDisturbChoice, until: null });
          saveDoNotDisturb(value as DoNotDisturbChoice).then(setState, (reason: unknown) => {
            setState(previous);
            setError(`Not saved: ${reason instanceof Error ? reason.message : String(reason)}`);
          });
        }}
        options={DO_NOT_DISTURB_CHOICES}
        value={state?.choice ?? 'off'}
      />
    </SettingRow>
  );
}

function ThisDevice({ phone }: { phone: boolean }) {
  return (
    <SettingsGroup title={phone ? 'This device' : 'This computer'}>
      <SystemNotificationsRow phone={phone} />
      <DoNotDisturbRow />
      <TestNotificationRow />
    </SettingsGroup>
  );
}

/**
 * Settings › Notifications (D17, phone 15): this device first, then for each type of event of
 * each plugin, a level (Off, List, Toast, System, Call), saved at once on every server.
 */
export function NotificationsSettingsSection({ phone = false }: { phone?: boolean }) {
  const [types, setTypes] = useState<EventTypeView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // A machine's server lists its linked cloud's types too, one row each.
    void apiFetch<EventTypeView[]>('/api/events/types').then(
      (listed) => !cancelled && setTypes(listed.filter((entry) => entry.type !== TEST_EVENT_TYPE)),
      (reason: unknown) =>
        !cancelled && setError(reason instanceof Error ? reason.message : String(reason)),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = (type: EventTypeView, delivery: EventDeliveryView) => {
    setTypes(
      (current) =>
        current?.map((entry) =>
          entry.type === type.type ? { ...entry, delivery, mixed: false } : entry,
        ) ?? null,
    );
    // Set on every server publishing it.
    void apiFetch('/api/events/preferences', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        type: type.type,
        delivery: delivery === type.defaultDelivery ? null : delivery,
      }),
    }).catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : String(reason)),
    );
  };

  return (
    <>
      <ThisDevice phone={phone} />
      {phone ? (
        <SettingsNote className="mt-0">
          For each event: Off, List (only in Notifications), Toast (in the app), System (also when
          the app is closed) or Call. The same choices apply on every device.
        </SettingsNote>
      ) : (
        <>
          <p className="mb-3 text-muted-foreground text-xs leading-5">
            {DELIVERY_LEVELS.map((level, index) => (
              <span key={level.value}>
                {index > 0 && ' · '}
                <strong className="font-semibold text-foreground">{level.label}</strong>{' '}
                {level.meaning}
              </span>
            ))}
          </p>
          <p className="mb-6 flex flex-wrap items-center gap-4 text-muted-foreground text-xs">
            {(['info', 'success', 'warning', 'error'] as const).map((tone) => (
              <span className="flex items-center gap-1.5" key={tone}>
                <ToneDot tone={tone} />
                {tone === 'warning' ? 'Waits on you' : tone.charAt(0).toUpperCase() + tone.slice(1)}
              </span>
            ))}
          </p>
        </>
      )}
      {error !== null && <p className="mb-4 text-destructive text-sm">{error}</p>}
      {types === null && error === null && (
        <p className="text-muted-foreground text-sm">Loading event types…</p>
      )}
      {types !== null &&
        groupBySource(types).map((group) => (
          <SettingsGroup
            key={group.title}
            title={
              <span className="flex items-center gap-2 text-foreground">
                <span
                  aria-hidden
                  className="flex size-5 items-center justify-center rounded-md bg-control font-semibold type-caption text-foreground"
                >
                  {group.title.charAt(0).toUpperCase()}
                </span>
                <span className="font-semibold">{group.title}</span>
                {!phone && (
                  <span className="font-normal text-muted-foreground">
                    · {group.types.length} {group.types.length === 1 ? 'type' : 'types'}
                  </span>
                )}
              </span>
            }
          >
            {group.types.map((type) => (
              <SettingRow
                description={
                  phone
                    ? undefined
                    : [
                        type.description,
                        type.mixed ? 'Set differently on Crowdex Cloud: choosing sets both' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || undefined
                }
                key={type.type}
                title={
                  // Its title, never its id (`run.failed`), which only the tooltip shows.
                  <span className="flex items-center gap-2" title={type.type}>
                    {!phone && <ToneDot tone={typeTone(type)} />}
                    {type.title}
                  </span>
                }
              >
                <SettingSelect
                  className={cn(
                    'min-w-24 justify-between',
                    type.delivery === 'off' && 'text-muted-foreground',
                  )}
                  label={type.title}
                  onValueChange={(value) => choose(type, value as EventDeliveryView)}
                  options={levelsFor(type.type)}
                  value={type.delivery}
                />
              </SettingRow>
            ))}
          </SettingsGroup>
        ))}
      {types !== null && !phone && (
        <p className="text-muted-foreground text-xs leading-5">
          Each plugin declares the events it publishes; new plugins add their section here. Agents
          can subscribe to the same events (Wakes on).
        </p>
      )}
    </>
  );
}
